import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';
import { useDispatch, useSelector } from 'react-redux';
import { selectIncident } from 'slices/commonSelectors';
import {
  addMemberToTeams,
  RemoveUserFromWorkspaceTeamSite,
} from 'actions/teamsActions';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useFetchIncidentWorkspaceTeamStatus } from '../useFetchIncidentWorkspaceTeamStatus';
import { workLogKeys } from './useWorkLog';
import { useSearchParams } from 'react-router';

import { incidentRosterKeys } from '../../components/IAP/hooks/incidentRoster/incidentRoster.queryKeys';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

// Query keys
export const keys = {
  incidentRoster: 'incidentRoster',
  groupRosters: 'groupRosters',
  incidentSchedule: 'incidentSchedule',
  userIncidentSchedule: 'userIncidentSchedule',
  incidentPositions: 'incidentPositions',
};

// Types
interface RootState {
  app: {
    user: {
      user_guid: string;
    };
  };
}

interface IncidentPosition {
  is_workgroup: boolean;
  id: string;
  item_id: string;
  name: string;
  parent_id: string | null;
  parent_name: string | null;
  is_template: boolean;
}

interface IncidentMember {
  user_guid: string;
  email_address?: string;
  incident_member_id?: string;
  seat?: string;
  is_leader?: boolean;
  [key: string]: any;
}

interface AddUserToIncidentRosterParams {
  positionAssignments: {
    positionId: string;
    positionName: string;
    incidentMemberUserId: string;
    isLeader: boolean;
    designation?: 'Primary' | 'Deputy' | 'Assistant' | null;
  }[];
}

interface AssignMemberToPositionInRosterParams {
  incidentMemberId: string;
  positionId: string;
  incidentId: string;
  groupId: string;
  isLeader?: boolean;
}

interface UpdatePositionAssignmentParams {
  member: IncidentMember;
  incidentMemberId: string;
  positionId: string;
  isLeader?: boolean;
  designation?: 'Primary' | 'Deputy' | 'Assistant' | null;
}

interface MarkIncidentUserActiveParams {
  user_guid: string;
  incident_member_id: string;
}

interface AddNonMemberToIncidentParams {
  incidentNonMember: any;
  incident_id: string;
}

export interface TeamMembership {
  name: string;
}

export interface ProfileSettings {
  is_new_user: string;
  userName: string;
  mobile_phone_number: string;
  home_phone_number: string;
  pager_address: string;
  opt_in_for_sms: boolean;
}

export interface GroupRosterItem {
  user_guid: string;
  email_address: string;
  currently_selected: any;
  profile_settings: ProfileSettings;
  user_name: string;
  group_guid: string;
  group_name: string;
  is_closed: boolean | null;
  default_settings: any;
  default_sender_email: null | string;
  risk_levels: string[];
  daily_digest_team: string;
  allow_admin_profile_edits: boolean;
  role_assignments: { name: string }[];
  assigned_incidents: {
    incident_id: string;
    incident_name: string;
    position_name: string;
  }[];
  unavailability: any[];
  home_aor: string | null;
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useGetRosterForIncident = (
  incidentId?: string
): UseQueryResult<RosterEntry[], Error> => {
  const actualIncidentId = useActualIncidentId();
  const resolvedIncidentId = incidentId || actualIncidentId;

  return useQuery<RosterEntry[], Error>({
    queryKey: [keys.incidentRoster, resolvedIncidentId],
    queryFn: async () => {
      if (!resolvedIncidentId) return [];
      const response = await axios(config.apiGateway.getRosterForIncident, {
        method: 'POST',
        data: {
          incident_id: resolvedIncidentId,
        },
      });
      return response.data || [];
    },
    enabled: !!resolvedIncidentId,
  });
};

export const useGetRostersForGroup = (
  groupGuid: string
): UseQueryResult<GroupRosterItem[], Error> => {
  const { user_guid } = useSelector((state: RootState) => state.app.user);

  return useQuery<GroupRosterItem[], Error>({
    queryKey: [keys.groupRosters, groupGuid],
    queryFn: async () => {
      if (!groupGuid) return [];
      const response = await axios(config.apiGateway.fetchRostersForGroups, {
        method: 'POST',
        data: {
          group_guids: [groupGuid],
          user_guid: user_guid,
        },
      });
      return response.data || [];
    },
    enabled: !!groupGuid && !!user_guid,
  });
};

export const useAddUsersToIncidentRoster = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();
  const { data: teamsStatus } = useFetchIncidentWorkspaceTeamStatus();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({
      positionAssignments,
    }: AddUserToIncidentRosterParams) => {
      const response = await axios(
        config.apiGateway.addMemberToIncidentRoster(incidentId),
        {
          method: 'POST',
          data: {
            positionAssignments,
            isDraft,
            groupId: groupGuid,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('User added to incident successfully');
      if (teamsStatus === 'done') {
        dispatch(
          addMemberToTeams(
            incidentId,
            variables.positionAssignments.map(
              (assignment) => assignment.incidentMemberUserId
            )
          )
        );
      }
    },
    onError: (error: any) => {
      if (error.response?.data === '{"ERROR":"User does not exist"}') {
        toast.error('User does not exist');
      } else if (
        error.response?.data === '{"ERROR":"User already in this Incident"}'
      ) {
        toast.error('User already in this Incident');
      } else {
        handleError(error, 'ERROR adding incident member');
      }
    },
  });
};

export const useAssignMemberToPositionInRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      positionId,
      isLeader,
    }: AssignMemberToPositionInRosterParams) => {
      const response = await axios(
        config.apiGateway.assignMemberToPositionInRoster(incidentId),
        {
          method: 'POST',
          data: {
            incidentMemberId,
            positionId,
            isLeader,
            isDraft,
            groupId,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Member assigned to position successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR assigning member to position in roster');
    },
  });
};

interface UpdateMemberPositionsInRosterParams {
  incidentMemberId: string;
  newPositions: {
    positionId: string;
    isLeader: boolean;
  }[];
}

export const useUpdateMemberPositionsInRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      newPositions,
    }: UpdateMemberPositionsInRosterParams) => {
      const response = await axios(
        config.apiGateway.updateMemberPositionsInRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            incidentId,
            incidentMemberId,
            newPositions,
            isDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Member positions updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to update member positions in roster');
    },
  });
};

interface RemoveMemberFromRosterParams {
  user: IncidentMember;
}

export const useRemoveMemberFromIncidentRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();
  const { teams_id: teamsId } = useSelector(selectIncident);
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async ({ user }: RemoveMemberFromRosterParams) => {
      const response = await axios(
        config.apiGateway.removeMemberFromIncidentRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            incidentMemberId: user.incident_member_id,
            isDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_response, { user }) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('User removed from incident successfully');
      if (teamsId) {
        dispatch(
          RemoveUserFromWorkspaceTeamSite(
            incidentId,
            user.email_address,
            'incident'
          )
        );
      }
    },
    onError: (error: any) => {
      handleError(error, 'Failed to remove member from incident roster');
    },
  });
};

interface RemoveMemberFromRosterPositionParams {
  incidentMemberId: string;
  positionId: string;
}

export const useRemoveMemberFromPositionInRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      positionId,
    }: RemoveMemberFromRosterPositionParams) => {
      const response = await axios(
        config.apiGateway.removeMemberFromPositionInRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            incidentMemberId,
            positionId,
            isDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Member removed from position successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR removing member from position in roster');
    },
  });
};

interface RemoveSelfFromRosterParams {
  user: IncidentMember;
}

export const useRemoveSelfFromRoster = (
  incidentId: string,
  groupId: string
) => {
  const queryClient = useQueryClient();
  const { teams_id: teamsId } = useSelector(selectIncident);
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (_params: RemoveSelfFromRosterParams) => {
      const response = await axios(
        config.apiGateway.removeSelfFromRoster(incidentId),
        {
          method: 'POST',
          data: {
            groupId,
            isDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_response, { user }) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Successfully left the incident');
      if (teamsId) {
        dispatch(
          RemoveUserFromWorkspaceTeamSite(
            incidentId,
            user.email_address,
            'incident'
          )
        );
      }
    },
    onError: (error: any) => {
      handleError(error, 'removeSelfFromIncidentError');
    },
  });
};

export const useMarkIncidentUserActive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ user_guid }: MarkIncidentUserActiveParams) => {
      const response = await axios(config.apiGateway.markIncidentMemberActive, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          user_guid,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: workLogKeys.byIncident(incidentId),
      });
      toast.success('User marked as active');
    },
    onError: (error: any) => {
      if (
        error.response?.data?.ERROR?.includes(
          'You can not work on two different incident at the same time.'
        )
      ) {
        toast.error(
          'User is already working on another incident. You can not work on two different incidents at the same time.',
          { autoClose: 10000 } as ToastOptions
        );
      } else {
        handleError(error, 'Failed to activate incident user');
      }
    },
  });
};

export const useMarkIncidentUserInactive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      user_guid,
      logout_time,
      minutes_worked,
      incident_member_id,
    }: {
      user_guid: string;
      logout_time: Date;
      minutes_worked: number;
      incident_member_id: string;
    }) => {
      const response = await axios(
        config.apiGateway.markIncidentMemberDeactive,
        {
          method: 'POST',
          data: {
            incident_id: incidentId,
            user_guid,
            logout_time,
            minutes_worked,
            group_guid: groupGuid,
            incident_member_id,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [keys.incidentRoster, incidentId],
        (oldData: any) => {
          if (!oldData) return undefined;
          return oldData.map((member: any) => {
            if (member.incident_member_id === variables.incident_member_id) {
              return {
                ...member,
                active: 0,
                active_time: null,
              };
            }
            return member;
          });
        }
      );

      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });

      queryClient.invalidateQueries({
        queryKey: workLogKeys.byIncident(incidentId),
      });

      toast.success('User marked as inactive');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to deactivate incident user');
    },
  });
};

export const useUpdatePositionAssignment = () => {
  const queryClient = useQueryClient();

  const incidentId = useActualIncidentId();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';

  return useMutation({
    mutationFn: async ({
      incidentMemberId,
      positionId,
      isLeader,
      designation,
    }: UpdatePositionAssignmentParams) => {
      const response = await axios(
        config.apiGateway.updatePositionAssignment(incidentId),
        {
          method: 'POST',
          data: {
            incidentId,
            incidentMemberId,
            positionId,
            isLeader,
            designation,
            draft: isDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [keys.incidentSchedule, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          keys.userIncidentSchedule,
          incidentId,
          variables.member.user_guid,
          variables.incidentMemberId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(incidentId),
      });
      toast.success('Incident member updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to edit incident member');
    },
  });
};

export const useAddNonMemberToIncident = (groupGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentNonMember,
      incident_id,
    }: AddNonMemberToIncidentParams) => {
      const response = await axios(config.apiGateway.addNonMemberToIncident, {
        method: 'POST',
        data: {
          incident_id,
          incidentNonMember,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, variables.incident_id],
      });
      toast.success('Non-member added to incident successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to add non-member to incident');
    },
  });
};

interface ToggleApprovalAuthorityParams {
  incident_id: string;
  target_user_guid: string;
  approval_authority: boolean;
}

export const useToggleApprovalAuthority = (
  incident_id: string,
  _group_guid?: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      target_user_guid,
      approval_authority,
    }: Omit<ToggleApprovalAuthorityParams, 'incident_id'>) => {
      const response = await axios(config.apiGateway.toggleApprovalAuthority, {
        method: 'POST',
        data: {
          incident_id,
          target_user_guid,
          approval_authority,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incident_id],
      });
      toast.success('Approval authority updated successfully');
    },
    onError: (error: any) => {
      const errorMessage = parseErrorMessage(error);
      if (errorMessage.includes('last remaining user')) {
        toast.error(
          'Cannot remove approval authority from the last remaining user'
        );
      } else if (errorMessage.includes('do not have approval authority')) {
        toast.error(
          'You do not have approval authority to perform this action'
        );
      } else {
        handleError(error, 'Failed to update approval authority');
      }
    },
  });
};

export const getPositionsForIncident = async (incidentId: string) => {
  const response = await axios.get<IncidentPosition[]>(
    `${config.apiGateway.incidentPositions}/${incidentId}`
  );
  return response.data || [];
};

export const useGetPositionsForIncident = () => {
  const resolvedIncidentId = useActualIncidentId();

  return useQuery({
    queryKey: [keys.incidentPositions, resolvedIncidentId],
    queryFn: () => getPositionsForIncident(resolvedIncidentId),
    enabled: !!resolvedIncidentId,
  });
};

export const useGetPositionsForIncidentId = (incidentId: string) => {
  return useQuery({
    queryKey: [keys.incidentPositions, incidentId],
    queryFn: () => getPositionsForIncident(incidentId),
    enabled: !!incidentId,
  });
};

export const useCreateNewPositionForIncident = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      name,
      reportsTo,
      isWorkgroup = false,
    }: {
      name: string;
      reportsTo?: string;
      isWorkgroup?: boolean;
    }) => {
      const response = await axios.post(config.apiGateway.incidentPositions, {
        incident_id: incidentId,
        name,
        reports_to: reportsTo,
        is_workgroup: isWorkgroup,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      toast.success('Position created successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to create new position');
    },
  });
};

export const useEditPositionForIncident = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      reportsTo,
      isWorkgroup = false,
    }: {
      id: string;
      name: string;
      reportsTo?: string;
      isWorkgroup?: boolean;
    }) => {
      const response = await axios.put(
        `${config.apiGateway.incidentPositions}/${id}`,
        {
          incident_id: incidentId,
          name,
          reports_to: reportsTo,
          is_workgroup: isWorkgroup,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('Position updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to edit position');
    },
  });
};

export const useDeletePositionForIncident = () => {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const response = await axios.delete(
        `${config.apiGateway.incidentPositions}/${id}`,
        {
          data: {
            incident_id: incidentId,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [keys.incidentPositions, incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: [keys.incidentRoster, incidentId],
      });
      toast.success('Position deleted successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to delete position');
    },
  });
};
